import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CustomInput from '@/components/common/CustomInput';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import MfaCodeInput, { MFA_CODE_LENGTH } from '@/components/common/MfaCodeInput';
import MfaSecretBox from '@/components/common/MfaSecretBox';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { RootState } from '@/store';
import {
  MfaSetupResponse,
  useConfirmMfaMutation,
  useDisableMfaMutation,
  useEnableMfaMutation,
  useGetMfaStatusQuery,
} from '@/store/api/mfaApi';
import { getErrorMessage, showToastMessage } from '@/utils';

const identitySchema = z.object({
  usernameOrEmail: z.string().trim().min(1, 'Enter your email'),
  password: z.string().min(1, 'Please enter a password'),
});
type IdentityForm = z.infer<typeof identitySchema>;

const cardClass =
  'bg-white rounded-xl p-6 border max-w-md shadow-[0_6px_12px_-6px_rgba(0,0,0,0.50)] text-black';
const buttonClass = 'mt-4 bg-green text-white px-4 py-1.5 rounded text-sm disabled:opacity-50';
const cancelClass = 'mt-4 px-4 py-1.5 rounded text-sm border border-green';
const codeInputClass = 'border-green bg-[#F6FFF8] text-black';

const getStatus = (err: unknown) => (err as { status?: number })?.status;
const getMfaError = (err: unknown) =>
  getStatus(err) === 403
    ? 'Two-factor authentication is not available for this account'
    : String(getErrorMessage(err));

interface MfaManagerProps {
  /** Skip the Enable/Disable button and go straight to that flow. */
  startOpen?: boolean;
  onDone?: () => void;
}

const MfaManager = ({ startOpen = false, onDone }: MfaManagerProps) => {
  const { canUseMfa } = useRoleCheck();
  const profile = useSelector((state: RootState) => state.user.data);
  const {
    data: status,
    isLoading,
    error: statusError,
    refetch,
  } = useGetMfaStatusQuery(undefined, { skip: !canUseMfa });
  const [enableMfa, { isLoading: isEnabling }] = useEnableMfaMutation();
  const [confirmMfa, { isLoading: isConfirming }] = useConfirmMfaMutation();
  const [disableMfa, { isLoading: isDisabling }] = useDisableMfaMutation();

  const [showIdentity, setShowIdentity] = useState(startOpen);
  const [showDisable, setShowDisable] = useState(startOpen);
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError: setFieldError,
  } = useForm<IdentityForm>({
    resolver: zodResolver(identitySchema),
    values: { usernameOrEmail: profile?.email || profile?.username || '', password: '' },
  });

  // MFA stays off until the confirm call, so warn before the key is lost.
  useEffect(() => {
    if (!setup) return undefined;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [setup]);

  const closeForms = () => {
    setSetup(null);
    setShowIdentity(false);
    setShowDisable(false);
    setCode('');
    setError(null);
    onDone?.();
  };

  const startSetup = async (data: IdentityForm) => {
    if (isEnabling) return;
    setError(null);
    try {
      setSetup(await enableMfa(data).unwrap());
      setCode('');
      reset();
    } catch (err) {
      if (getStatus(err) === 401) {
        setFieldError('password', { message: String(getErrorMessage(err)) });
      } else {
        setError(getMfaError(err));
      }
    }
  };

  const confirmSetup = async () => {
    if (isConfirming) return;
    setError(null);
    try {
      await confirmMfa({ token: code }).unwrap();
      await refetch();
      closeForms();
      showToastMessage('success', 'Two-factor authentication enabled');
    } catch (err) {
      setCode('');
      setError(
        getStatus(err) === 401
          ? `${getMfaError(err)}. Make sure your phone's time is set automatically.`
          : getMfaError(err)
      );
    }
  };

  const submitDisable = async () => {
    if (isDisabling) return;
    setError(null);
    try {
      await disableMfa({ token: code }).unwrap();
      await refetch();
      closeForms();
      showToastMessage('success', 'Two-factor authentication disabled');
    } catch (err) {
      setCode('');
      setError(getMfaError(err));
    }
  };

  const isCodeComplete = code.length === MFA_CODE_LENGTH;

  return (
    <div className="space-y-4">
      {statusError && <ErrorBanner type="error" message={getMfaError(statusError)} />}
      {error && <ErrorBanner type="error" title="Error" message={error} />}
      {(isLoading || !canUseMfa) && !statusError && (
        <p className="text-sm text-gray-500">Loading...</p>
      )}

      {setup && (
        <div className={cardClass}>
          <h2 className="font-semibold text-lg">Scan the QR code</h2>
          <p className="text-sm text-gray-500 mb-4">
            Open your authenticator app, add an account, scan this code.
          </p>
          <img src={setup.qrCode} alt="MFA QR code" className="mx-auto h-48 w-48" />

          <MfaSecretBox secret={setup.secret} />
          <p className="mt-2 text-sm text-red-600">
            Save the manual key somewhere safe. There are no recovery codes. If you lose your
            device, another Owner must reset your MFA.
          </p>

          <label className="mt-4 block text-sm font-medium text-gray-700">Authenticator code</label>
          <MfaCodeInput
            value={code}
            onChange={setCode}
            className={`mt-2 ${codeInputClass}`}
            autoFocus
          />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={closeForms} className={cancelClass}>
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmSetup}
              disabled={!isCodeComplete || isConfirming}
              className={buttonClass}
            >
              {isConfirming ? 'Confirming...' : 'Confirm'}
            </button>
          </div>
        </div>
      )}

      {!setup && status && !status.isMfaEnabled && !showIdentity && (
        <div className={cardClass}>
          <h2 className="font-semibold text-lg">Two-factor authentication is OFF</h2>
          <p className="text-sm text-gray-500">
            Use an authenticator app to protect your account at login.
          </p>
          <div className="flex justify-end">
            <button type="button" onClick={() => setShowIdentity(true)} className={buttonClass}>
              Enable
            </button>
          </div>
        </div>
      )}

      {!setup && status && !status.isMfaEnabled && showIdentity && (
        <form onSubmit={handleSubmit(startSetup)} className={cardClass}>
          <h2 className="font-semibold text-lg">Confirm your identity</h2>
          <p className="text-sm text-gray-500 mb-4">
            Enter your account email and password to continue.
          </p>
          <CustomInput
            name="usernameOrEmail"
            control={control}
            label="Email"
            type="text"
            placeholder="Enter your email"
            autoComplete="username"
          />
          <CustomInput
            name="password"
            control={control}
            label="Password"
            type="password"
            placeholder="Enter your password"
            autoComplete="current-password"
          />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={closeForms} className={cancelClass}>
              Cancel
            </button>
            <button type="submit" disabled={isEnabling} className={buttonClass}>
              {isEnabling ? 'Please wait...' : 'Continue'}
            </button>
          </div>
        </form>
      )}

      {status?.isMfaEnabled && (
        <div className={cardClass}>
          <h2 className="font-semibold text-lg">Two-factor authentication is ON</h2>
          {showDisable ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Enter the current 6-digit code from your authenticator app.
              </p>
              <MfaCodeInput value={code} onChange={setCode} className={codeInputClass} autoFocus />
              <div className="flex justify-end gap-3">
                <button type="button" onClick={closeForms} className={cancelClass}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitDisable}
                  disabled={!isCodeComplete || isDisabling}
                  className={buttonClass}
                >
                  {isDisabling ? 'Please wait...' : 'Disable'}
                </button>
              </div>
            </>
          ) : (
            <div className="flex justify-end">
              <button type="button" onClick={() => setShowDisable(true)} className={buttonClass}>
                Disable
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MfaManager;
